import React from "react";

type Props = { children: React.ReactNode };
type State = { hasError: boolean; message?: string; error?: Error };

/**
 * Global Error Boundary
 * Catches React component errors and prevents white-screen crashes
 */
export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, message: undefined, error: undefined };
  }

  static getDerivedStateFromError(error: any) {
    // Ensure we handle both Error objects and other types
    const errorObj = error instanceof Error ? error : new Error(String(error));
    return {
      hasError: true,
      message: errorObj.message || String(error),
      error: errorObj,
    };
  }

  componentDidCatch(error: any, info: any) {
    console.error("React ErrorBoundary caught error:", error);
    console.error("Error name:", error?.name);
    console.error("Error message:", error?.message);
    console.error("Error stack:", error?.stack);
    console.error("Component stack:", info?.componentStack);
    
    // Log to console with more details for debugging
    if (error?.message?.includes('constructor') || error?.message?.includes('is not a constructor')) {
      console.error("CONSTRUCTOR ERROR DETECTED - This is likely a minification/bundling issue");
      console.error("Try clearing browser cache and rebuilding the application");
    }
  }

  handleRefresh = () => {
    this.setState({ hasError: false, message: undefined });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6" data-testid="error-boundary">
          <div className="card bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 p-4 rounded-lg">
            <div className="font-semibold text-red-900 dark:text-red-100 mb-2">
              Something went wrong
            </div>
            <div className="text-sm text-red-700 dark:text-red-200 opacity-80 mb-3">
              A component error occurred. Please refresh the page or try again.
            </div>
            <div className="text-xs text-red-600 dark:text-red-300 font-mono bg-red-100 dark:bg-red-900 p-2 rounded overflow-auto max-h-40 mb-3">
              {this.state.message}
            </div>
            <button 
              onClick={this.handleRefresh}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium"
              data-testid="button-refresh"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
